import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Star as StarIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import globalCuisineBackground from '../images/global-cuisine.png';
import ItalianImage from '../images/Italian.jpg';
import FrenchImage from '../images/french.jpg';
import ChineseImage from '../images/chinese.jpeg';
import JapaneseImage from '../images/japanese.webp';
import IndianImage from '../images/indian.jpg';
import MexicanImage from '../images/mexican.jpg';
import ThaiImage from '../images/thai.webp';
import GreekImage from '../images/greek.webp';
import SpanishImage from '../images/spanish.jpg';
import KoreanImage from '../images/korean.jpg';

const PAGE_URL = 'https://createadish.com/global-cuisine';
const PAGE_TITLE = 'Global Cuisine 101: A World of Flavors at Your Fingertips - Create A Dish';
const PAGE_DESCRIPTION = 'Journey through the diverse culinary landscapes available in our generator. This directory breaks down the signature spices, traditional techniques, and historical backgrounds of international cuisines, helping you understand the "why" behind the flavors you create.';
const PAGE_IMAGE = 'https://createadish.com/images/global-cuisine-og.png';

const GlobalCuisine: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="global cuisine, international cooking, world flavors, culinary traditions, cooking techniques, ethnic cuisine, cultural cooking, food culture" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={PAGE_URL} />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={PAGE_URL} />
        
        {/* Structured Data - Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Global Cuisine 101: A World of Flavors at Your Fingertips",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": {
              "@type": "Organization",
              "name": "Create A Dish"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Create A Dish",
              "logo": {
                "@type": "ImageObject",
                "url": "https://createadish.com/logo.png"
              }
            },
            "datePublished": "2025-12-24",
            "dateModified": "2025-12-24",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": PAGE_URL
            }
          })}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        {/* Hero Section */}
        <Box
          sx={{
            height: { xs: '40vh', md: '50vh' },
            backgroundImage: `url(${globalCuisineBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(1px)',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 2,
              }}
            >
              Global Cuisine 101
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 1,
              }}
            >
              A World of Flavors at Your Fingertips
            </Typography>
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Global Cuisine
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Explore World Cuisines
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Journey through the diverse culinary landscapes available in our generator. This directory breaks down the signature spices, traditional techniques, and historical backgrounds of international cuisines, helping you understand the "why" behind the flavors you create. At CreateADish.com, our AI draws inspiration from these global traditions to help you craft unique recipes. Whether you're exploring bold spices or time-honored methods, this guide equips you to appreciate and recreate authentic tastes at home.
          </Typography>

          {/* Italian Cuisine */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Italian Cuisine
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Italian cuisine boasts a rich history dating back to ancient Rome, where trade routes with Arabs introduced citrus and spices around the first century BC, and Romans highly valued peppers. Over centuries, it evolved through regional influences, blending simple, fresh ingredients into globally beloved dishes.
            </Typography>
            <Box component="img" src={ItalianImage} alt="Italian cuisine" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Signature Spices and Herbs
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Basil: Essential for pesto and tomato-based sauces, adding a fresh, aromatic note.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Oregano: Commonly used in pizzas and marinades for its earthy, slightly bitter flavor.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Rosemary: Infuses roasted meats and potatoes with a pine-like aroma.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Garlic: A staple for depth in sauces and dressings.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Parsley, sage, thyme, and fennel: Provide herbal balance in various dishes.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Historical spices like coriander and cumin from Roman times, though less common today.</Typography>
            </Box>

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Traditional Techniques
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Pasta making: Hand-rolling dough and boiling al dente for texture.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Slow simmering: For ragù sauces to develop deep flavors.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Grilling and roasting: Simple methods to highlight fresh ingredients like vegetables and meats.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Preservation: Curing meats and pickling vegetables for longevity.</Typography>
            </Box>
          </Box>

          {/* French Cuisine */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              French Cuisine
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              French cuisine's history traces to the Middle Ages, emphasizing local, seasonal produce, with Celtic influences in fermentation and herb use. It rose to prominence through royal courts and chefs like Escoffier, shaping global fine dining.
            </Typography>
            <Box component="img" src={FrenchImage} alt="French cuisine" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Signature Spices and Herbs
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Thyme: Adds earthy notes to stews and roasts.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Tarragon: Key in béarnaise sauce for its anise-like flavor.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Bay leaves: Used in bouquets garnis for subtle aroma in stocks.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Nutmeg and cloves: For warming béchamel sauces.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Pepper and mustard: Essential for balancing flavors.</Typography>
            </Box>

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Traditional Techniques
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Sautéing: Quick cooking in butter for delicate flavors.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Braising: Slow-cooking meats in wine for tenderness.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Reduction: Concentrating sauces for intensity.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Pastry making: Layering dough for croissants and tarts.</Typography>
            </Box>
          </Box>

          {/* Chinese Cuisine */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Chinese Cuisine
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Chinese cuisine spans millennia, with regional variations like spicy Western styles from Sichuan, influenced by ancient fermentation and trade. It emphasizes balance of flavors, textures, and colors, rooted in Confucian principles.
            </Typography>
            <Box component="img" src={ChineseImage} alt="Chinese cuisine" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Signature Spices and Herbs
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Star anise: Adds licorice notes to braises.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Sichuan peppercorns: For numbing heat in spicy dishes.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Ginger, garlic, and scallions: Form the aromatic base.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Five-spice powder: Blend of cinnamon, cloves, fennel, star anise, and pepper.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Soy sauce and oyster sauce: For umami depth.</Typography>
            </Box>

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Traditional Techniques
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Stir-frying: High-heat quick cooking to retain crispness.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Steaming: Preserves nutrients in dim sum and fish.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Braising: In soy-lard mixes for rich flavors.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Stewing, frying, and smoking: For diverse textures.</Typography>
            </Box>
          </Box>

          {/* Japanese Cuisine */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Japanese Cuisine
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Japanese cuisine evolved from ancient rice farming and Buddhist influences, focusing on seasonality and simplicity, with global adaptations post-WWII.
            </Typography>
            <Box component="img" src={JapaneseImage} alt="Japanese cuisine" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Signature Spices and Herbs
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Shichimi togarashi (seven-spice): Chili, sesame, sansho pepper, etc.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Wasabi: Pungent heat for sushi.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Ginger: Pickled for cleansing the palate.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Sesame seeds and seaweed: For nutty, umami flavors.</Typography>
            </Box>

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Traditional Techniques
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Grilling (yakitori): Charcoal for smoky essence.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Simmering (nimono): Gentle cooking in dashi.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Raw preparation (sashimi): Emphasizing freshness.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Deep-frying (tempura): Light batter for crispness.</Typography>
            </Box>
          </Box>

          {/* Indian Cuisine */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Indian Cuisine
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Indian cuisine's history dates to ancient civilizations, with spices like turmeric cultivated for thousands of years, influenced by invasions and trade.
            </Typography>
            <Box component="img" src={IndianImage} alt="Indian cuisine" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Signature Spices and Herbs
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Turmeric: For color and anti-inflammatory properties.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Cumin, coriander, cardamom: Core to curries.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Chili powder, black pepper: For heat.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Garam masala: Blend of warming spices like cinnamon and cloves.</Typography>
            </Box>

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Traditional Techniques
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Tempering (tadka): Frying spices in oil to release aromas.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Slow-cooking curries: For flavor melding.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Tandoor baking: Clay oven for breads and meats.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Fermentation: In dosas and pickles.</Typography>
            </Box>
          </Box>

          {/* Mexican Cuisine */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Mexican Cuisine
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Mexican cuisine is a UNESCO-recognized heritage, blending indigenous Mesoamerican staples with Spanish influences post-conquest.
            </Typography>
            <Box component="img" src={MexicanImage} alt="Mexican cuisine" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Signature Spices and Herbs
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Cumin: Earthy base for many dishes.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Oregano: Mexican variety for tang.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Chili peppers (ancho, chipotle): For varied heat.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Cinnamon and cloves: In moles for complexity.</Typography>
            </Box>

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Traditional Techniques
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Nixtamalization: Alkaline treatment of corn for tortillas.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Mole making: Blending chiles and spices into sauces.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Grilling (asada): For meats with marinades.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Steaming tamales: Wrapped in corn husks.</Typography>
            </Box>
          </Box>

          {/* Thai Cuisine */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Thai Cuisine
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Thai cuisine originated from indigenous ingredients like lemongrass, evolving through Indian and Chinese influences, with adaptations for global palates.
            </Typography>
            <Box component="img" src={ThaiImage} alt="Thai cuisine" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Signature Spices and Herbs
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Lemongrass, galangal, kaffir lime: Aromatic trio for curries.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Chilies: For balanced heat.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Coriander, cumin, turmeric: In spice pastes.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Basil (Thai variety): Fresh finish.</Typography>
            </Box>

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Traditional Techniques
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Pounding pastes: Mortar and pestle for curries.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Stir-frying: Quick with high heat.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Steaming and grilling: For fresh flavors.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Balancing sweet, sour, salty, spicy.</Typography>
            </Box>
          </Box>

          {/* Greek Cuisine */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Greek Cuisine
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Greek cuisine roots in ancient times, with herbs like oregano central since antiquity, influenced by Mediterranean trade and conquests.
            </Typography>
            <Box component="img" src={GreekImage} alt="Greek cuisine" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Signature Spices and Herbs
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Oregano, thyme, rosemary: Herb staples.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Cinnamon, cumin, coriander: For warmth.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Allspice and sumac: Tangy accents.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Garlic and onion: Base aromatics.</Typography>
            </Box>

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Traditional Techniques
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Grilling: For souvlaki with olive oil.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Baking in phyllo: Layered pastries.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Stewing: Slow-cooked meats with herbs.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Pickling: Vegetables in vinegar.</Typography>
            </Box>
          </Box>

          {/* Spanish Cuisine */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Spanish Cuisine
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Spanish cuisine reflects diverse geography, from Moorish introductions of spices like saffron to regional fresh ingredients.
            </Typography>
            <Box component="img" src={SpanishImage} alt="Spanish cuisine" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Signature Spices and Herbs
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Paprika (pimentón): Smoky for chorizo.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Saffron: Golden hue in paella.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Cumin, oregano, parsley: Common herbs.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Garlic and bay leaves: For depth.</Typography>
            </Box>

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Traditional Techniques
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Tapas-style sharing: Small plates.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Paella cooking: In wide pans over open fire.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Curing (jamón): Air-drying meats.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Sofrito base: Sautéed onions, tomatoes, garlic.</Typography>
            </Box>
          </Box>

          {/* Korean Cuisine */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Korean Cuisine
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Korean cuisine developed through dynasties, with fermentation key since ancient times, and spices like chili introduced in the 16th century.
            </Typography>
            <Box component="img" src={KoreanImage} alt="Korean cuisine" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Signature Spices and Herbs
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Gochugaru (chili flakes): For heat.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Gochujang (chili paste): Fermented umami.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Sesame oil and seeds: Nutty flavor.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Garlic, ginger, scallions: Aromatics.</Typography>
            </Box>

            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Traditional Techniques
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>Fermentation (kimchi): For probiotics and tang.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Grilling (bulgogi): Marinated meats.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Stirring in stone pots (bibimbap): Mixed rice.</Typography>
              <Typography component="li" sx={{ mb: 1 }}>Stewing and boiling: For soups.</Typography>
            </Box>
          </Box>

          <Typography variant="body1" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', fontSize: '1.1rem' }}>
            Dive into these cuisines with our AI generator to mix and match elements for your next meal. Explore, create, and savor the world's flavors!
          </Typography>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Ready to Create Your Perfect Dish?
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<StarIcon />}
              onClick={handleGetStarted}
              sx={{
                minWidth: 200,
                fontSize: '1.1rem',
                py: 1.5,
                px: 4,
              }}
            >
              Get Started
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default GlobalCuisine;
